import { useCallback, useEffect, useRef, useState } from 'react';
import { useMapboxToken, type MapboxTokenSource } from './useMapboxToken';
import { searchMapboxAddresses, type MapboxFeature } from '@/lib/mapbox/geocoding';

/**
 * Headless address-autocomplete hook.
 *
 * Owns the Mapbox token, the typed query, a 300ms debounce, and the resulting
 * suggestions. Queries shorter than 3 characters clear suggestions and never
 * hit the network. The debounce timer is torn down on unmount so a late fetch
 * can't set state on an unmounted consumer.
 */

const DEBOUNCE_MS = 300;
const MIN_QUERY_LENGTH = 3;

export interface UseAddressAutocompleteOptions {
  /** When false, the token is not fetched (lazy-load until the field is used). */
  enabled?: boolean;
  /** Location bias passed through to the geocoder. */
  proximity?: { lng: number; lat: number };
  /**
   * Where the Mapbox token comes from. Default `authenticated` (admin surfaces).
   * Pass `public` on the unauthenticated donor page so the token comes from the
   * public `/api/public/mapbox-token/{subsidiary}` route.
   * A build-time `VITE_MAPBOX_TOKEN` still short-circuits both.
   */
  tokenSource?: MapboxTokenSource;
  /** Subsidiary code for the `public` token route. Defaults to the build-time subsidiary. */
  tokenSubsidiary?: string;
}

export interface UseAddressAutocompleteReturn {
  query: string;
  setQuery: (next: string) => void;
  suggestions: MapboxFeature[];
  loading: boolean;
  clear: () => void;
  tokenError: string | null;
}

export function useAddressAutocomplete(
  options: UseAddressAutocompleteOptions = {},
): UseAddressAutocompleteReturn {
  const { enabled = true, proximity, tokenSource = 'authenticated', tokenSubsidiary } = options;

  // Prefer a build-time env token (mirrors FieldOpsView): when present it
  // short-circuits the server endpoint, so local dev and any build that ships
  // the token work without the server token route being configured.
  const envToken = import.meta.env.VITE_MAPBOX_TOKEN as string | undefined;
  const fetchServerToken = enabled && !envToken;
  // Keep the authenticated (default) call in the legacy boolean form so nothing
  // about the admin path changes; only the public path uses the options object.
  const { token: serverToken, error: tokenError } = useMapboxToken(
    tokenSource === 'public'
      ? { enabled: fetchServerToken, source: 'public', subsidiary: tokenSubsidiary }
      : fetchServerToken,
  );
  const token = envToken || serverToken;

  const [query, setQueryState] = useState('');
  const [suggestions, setSuggestions] = useState<MapboxFeature[]>([]);
  const [loading, setLoading] = useState(false);

  const debounceRef = useRef<ReturnType<typeof setTimeout> | null>(null);

  // Read token/proximity through refs inside the debounced callback so a token
  // arriving (or the user moving) mid-type doesn't need to re-create setQuery.
  const tokenRef = useRef(token);
  tokenRef.current = token;
  const proximityRef = useRef(proximity);
  proximityRef.current = proximity;

  const clearTimer = () => {
    if (debounceRef.current) {
      clearTimeout(debounceRef.current);
      debounceRef.current = null;
    }
  };

  const setQuery = useCallback((next: string) => {
    setQueryState(next);
    clearTimer();

    if (next.length < MIN_QUERY_LENGTH) {
      setSuggestions([]);
      setLoading(false);
      return;
    }

    setLoading(true);
    debounceRef.current = setTimeout(async () => {
      const results = await searchMapboxAddresses(next, tokenRef.current, {
        proximity: proximityRef.current,
      });
      setSuggestions(results);
      setLoading(false);
    }, DEBOUNCE_MS);
  }, []);

  const clear = useCallback(() => {
    clearTimer();
    setQueryState('');
    setSuggestions([]);
    setLoading(false);
  }, []);

  // Tear down any pending debounce on unmount.
  useEffect(() => clearTimer, []);

  return {
    query,
    setQuery,
    suggestions,
    loading,
    clear,
    tokenError,
  };
}

export default useAddressAutocomplete;
